// ─────────────────────────────────────────────────────────────────────────────
// Listas de apoio do formulário de formalização.
//
// Apesar do nome antigo ("carregarListasMySuite"), estes dados NÃO vêm do
// MySuite: vêm do Supabase, schema orion. São quatro tabelas:
//   ocorrencia               → o que aconteceu
//   criticidade              → o quanto pesa
//   ocorrencia_criticidade   → quais criticidades cada ocorrência aceita
//   categorias_peso          → as classificações (com o peso do GUT)
//
// Carregadas uma vez por sessão e mantidas em memória.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase, tabela } from './supabase';

let cache = null;

/**
 * Carrega as quatro listas de uma vez.
 * Devolve { ocorrencias, criticidades, ocorrenciaCriticidades, classificacoes }.
 * Uma tabela que falhar volta como lista vazia — o formulário ainda abre, só
 * sem aquela lista, em vez de a tela inteira morrer.
 */
export async function carregarListas({ recarregar = false } = {}) {
  if (cache && !recarregar) return cache;

  const [resOcor, resCrit, resRel, resCateg] = await Promise.all([
    supabase.from('ocorrencia').select('oco_codigo, oco_descricao, ordem').eq('oco_ativo', 1).order('ordem'),
    supabase.from('criticidade').select('cri_codigo, cri_descricao').eq('cri_ativo', 1).order('cri_codigo'),
    supabase.from('ocorrencia_criticidade').select('oco_codigo, cri_codigo, ordem').order('ordem'),
    supabase.from('categorias_peso').select('codigo, nome_categoria, peso').order('nome_categoria'),
  ]);

  if (resOcor.error) console.error('[Orion] Ocorrências:', resOcor.error.message);
  if (resCrit.error) console.error('[Orion] Criticidades:', resCrit.error.message);
  if (resRel.error) console.error('[Orion] Relacionamentos ocorrência×criticidade:', resRel.error.message);
  if (resCateg.error) console.error('[Orion] Categorias/Classificações:', resCateg.error.message);

  cache = {
    ocorrencias: (resOcor.data || []).map(o => ({
      codigo: o.oco_codigo, nome: o.oco_descricao, exige_criticidade: '1',
    })),
    criticidades: (resCrit.data || []).map(c => ({
      codigo: c.cri_codigo, nome: c.cri_descricao,
    })),
    ocorrenciaCriticidades: (resRel.data || []).map(r => ({
      ocorrencia: r.oco_codigo, criticidade: r.cri_codigo,
    })),
    classificacoes: (resCateg.data || []).map(c => ({
      codigo: c.codigo, nome: (c.nome_categoria || '').trim(), peso: c.peso,
    })),
  };

  return cache;
}

/**
 * Ordena por nome respeitando a acentuação do português.
 *
 * `String(...)` porque uma linha com descrição nula no banco derrubava a tela
 * inteira num TypeError — e uma lista fora de ordem é muito menos grave que
 * uma aba que não abre.
 */
export function ordenarPorNome(lista) {
  return [...(lista || [])].sort((a, b) =>
    String(a?.nome || '').localeCompare(String(b?.nome || ''), 'pt-BR'));
}

/**
 * Criticidades válidas para uma ocorrência.
 * Devolve { lista, obrigatoria, mensagem }:
 *   lista       → o que oferecer no select
 *   obrigatoria → false quando a ocorrência não exige criticidade
 *   mensagem    → texto do placeholder quando a lista vem vazia
 */
export function criticidadesDaOcorrencia(listas, codigoOcorrencia) {
  if (!codigoOcorrencia) {
    return { lista: [], obrigatoria: true, mensagem: 'Selecione a ocorrência primeiro...' };
  }

  // O código vem do <select> como texto e no banco é número — por isso a
  // conversão dos dois lados, em vez de comparar com `==` frouxo.
  const codigosValidos = listas.ocorrenciaCriticidades
    .filter(r => Number(r.ocorrencia) === Number(codigoOcorrencia))
    .map(r => r.criticidade);

  const filtradas = listas.criticidades.filter(c => codigosValidos.includes(c.codigo));
  if (filtradas.length) return { lista: ordenarPorNome(filtradas), obrigatoria: true, mensagem: '' };

  const ocorrencia = listas.ocorrencias.find(o => Number(o.codigo) === Number(codigoOcorrencia));
  if (ocorrencia && ocorrencia.exige_criticidade !== '1') {
    return { lista: [], obrigatoria: false, mensagem: 'Não exige criticidade' };
  }
  return { lista: [], obrigatoria: true, mensagem: 'Nenhuma criticidade vinculada' };
}

/** Sistemas cadastrados (select da aba Incidentes). */
export async function carregarSistemas() {
  try {
    const { data, error } = await tabela('sistemas')
      .select('sis_codigo, sis_nome')
      .order('sis_nome');
    if (error) throw error;
    return (data || []).map(s => ({ codigo: s.sis_codigo, nome: (s.sis_nome || '').trim() }));
  } catch (err) {
    console.warn('[Orion] Falha ao carregar sistemas:', err.message);
    return [];
  }
}
